import { useTranslation } from 'react-i18next'

import { CircleFlag } from '@/components/ui/CircleFlag'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxSearch,
  ComboboxTrigger,
  ComboboxValue,
} from '@/components/ui/Combobox'
import { type ControlSize } from '@/components/ui/control'
import { countryOptions, findCountry, countryName } from '@/lib/countries'

/* The country dropdown — ISO 3166 compliant, per the brief.
 *
 * A picker over the 249 officially assigned codes (lib/countries.ts). The stored
 * value is the alpha-2 code; the circular flag and the localised name are the
 * label, and the code repeats as the hint so a document-facing value is visible
 * on the row rather than only inferable from the flag.
 *
 * Searchable is not optional here as it is on Select. Two hundred and forty-nine
 * rows cannot be scanned, and the filter matches EN name, RU name, alpha-2,
 * alpha-3 and numeric at once (see countryOptions), so "kaz", "398", "Казахстан"
 * and "Kazakhstan" all land on the same row whichever language the interface is
 * in.
 *
 * ── The shape, which changed ──
 *
 * This control used to BE a text input: the box on the form was the field, and
 * typing in it filtered in place. It is now a BUTTON that opens a popup with the
 * search field at the top of the list — the shape of the shadcn country dropdown
 * it was asked to match, and the same shape as this project's own Select.
 *
 * Two things follow from that, and both are improvements rather than costs. The
 * closed control can now carry the FLAG beside the name, because it is markup
 * and not the value of an `<input>` — the old version had to keep the glyph out
 * of the text so a user could type over it. And the box no longer accepts
 * arbitrary typing, so it cannot be left holding a half-typed country that was
 * never committed.
 *
 * ── The stored-value problem, which is the substantive thing in this file ──
 *
 * `origin` and `destination` predate this control. Migration 0020 defines them as
 * bounded non-blank text meaning POL → border → POD, and what is in the column
 * today is PLACES: "Shanghai", "Dostyk", "Ashgabat". None of those is a country
 * code, so on a saved route option this picker has a value that is not among its
 * options — and a picker in that state renders an empty box, which over a saved
 * routing reads as "no origin agreed" rather than "this was entered before the
 * field became a picker".
 *
 * So a value that is not one of the 249 is offered as its own first row, kept
 * selectable and marked as carried over. incoterms.ts does exactly this for a
 * stored term outside the eleven, and the reasoning transfers: nothing here may
 * reject or silently drop data that is already saved. What it changes is only
 * what is offered NEXT.
 *
 * The consequence to be aware of when reading the rest of the quote surface:
 * a route option created through this control now holds a COUNTRY where older
 * rows hold a port, so OptionRow's "Shanghai → Dostyk → Ashgabat" line reads
 * "CN → KZ → TM" for new records. That is the field the brief asked for; the
 * port is no longer captured anywhere on the option.
 */

export interface CountryDropdownProps {
  /** ISO 3166-1 alpha-2, or a legacy free-text place. Empty for unset. */
  value: string
  onChange: (value: string) => void
  /** Shrinks the CLOSED control to the flag alone, for a country that sits
   *  beside a field carrying the actual answer — a leg's route, where the place
   *  is "Khorgos" and the country is a second fact that must not compete with
   *  it for the line.
   *
   *  Visual only. The country's name is still read out (see the aria-label
   *  built below) and is still the label of every row in the list, because a
   *  control whose whole value is a 18px picture is unusable by anyone not
   *  looking at it. */
  flagOnly?: boolean
  id?: string
  name?: string
  disabled?: boolean
  placeholder?: string
  size?: ControlSize
  className?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  'aria-label'?: string
}

/* Base UI: none directly — this is composed from Combobox.tsx, which is the
   `@base-ui/react` combobox primitive wearing this system's tokens. */
export function CountryDropdown({
  value,
  onChange,
  flagOnly,
  id,
  name,
  disabled,
  placeholder,
  size = 'md',
  className,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
  'aria-label': ariaLabel,
}: CountryDropdownProps) {
  const { t, i18n } = useTranslation()
  const language = i18n.language

  const stored = value.trim()
  const selected = findCountry(stored)
  /** Saved, non-blank, and not a country: a place from before the picker. */
  const carriedOver = stored !== '' && !selected

  /* Built per render, without useMemo.

     Not a performance oversight — the memoized version was written first and
     removed. `eslint-plugin-react-hooks`' compiler-backed rules reject a useMemo
     whose dependency is a local derived by `.trim()`
     (react-hooks/preserve-manual-memoization): it cannot prove the value stable,
     so the whole component drops out of optimisation and the manual memo buys
     nothing while costing that.

     Cheap enough to leave alone: 249 objects and a Map, only while a route-option
     sheet is mounted. If this ever needs memoizing, hoist the language-keyed list
     to a module-level cache rather than reinstating the hook. */
  const options = [
    ...(carriedOver
      ? [
          {
            value: stored,
            label: stored,
            hint: t('common.form.countryCarriedOver'),
            search: stored.toLowerCase(),
          },
        ]
      : []),
    ...countryOptions(language),
  ]

  const byCode = new Map(options.map((option) => [option.value, option]))

  /* What the control ANSWERS TO, which in flag-only mode is the only place the
     country is spoken.

     Composed here rather than left to the caller because the caller knows the
     field ("Origin country") and this knows the value ("Kazakhstan"), and an
     accessible name carrying only one of the two is either an unlabelled
     control or an unreadable value. When the flag is accompanied by its name on
     screen, the caller's own label is enough and this stays out of the way. */
  const selectedName = selected
    ? countryName(selected, language)
    : carriedOver
      ? stored
      : ''
  const triggerLabel =
    flagOnly && selectedName
      ? [ariaLabel, selectedName].filter(Boolean).join(': ')
      : ariaLabel

  /* The item VALUES are the alpha-2 code strings, not the option objects.
     Base UI compares the selected value against item values with `Object.is`
     unless `isItemEqualToValue` is supplied, and a code string compares by
     identity for free — passing objects here would have meant either a custom
     comparator or a value that never matched on the first render, which is
     precisely the empty-looking control this component exists to avoid.

     The option objects stay reachable through `byCode`, which is what the
     filter and the row renderer look up — the list Base UI walks is codes. */
  return (
    <Combobox
      items={options.map((option) => option.value)}
      value={stored === '' ? null : stored}
      onValueChange={(next: string | null) => onChange(next ?? '')}
      itemToStringLabel={(code: string) => {
        const country = findCountry(code)
        return country ? countryName(country, language) : code
      }}
      /* One keystroke matches EN name, RU name, alpha-2, alpha-3 and numeric.
         The default filter sees only the label, so "RUS" or "398" — the forms
         a customs document uses — would answer "no matches" over a list that
         contains the country. */
      filter={(code: string, query: string) => {
        const needle = query.trim().toLowerCase()
        if (needle === '') return true
        return byCode.get(code)?.search.includes(needle) ?? false
      }}
      disabled={disabled}
      // The first match highlights as you type, so Enter commits the obvious
      // answer without an arrow press — the behaviour a 249-row list needs and
      // a five-row mode list does not.
      autoHighlight
    >
      <ComboboxTrigger
        id={id}
        triggerSize={size}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        aria-label={triggerLabel}
        className={className}
      >
        {/* Base UI renders no element for Value; the flag and the name are ours.
            `aria-hidden` on the flag, because the trigger's accessible name
            already carries the country — see CircleFlag. */}
        <ComboboxValue>
          {(code: string | null) => {
            const country = findCountry(code)
            if (code == null || code === '') {
              return (
                <span className="text-text-faint truncate">
                  {/* Even flag-only needs a placeholder: an empty box with a
                      chevron says nothing about what it would offer. The globe
                      CircleFlag falls back to is that placeholder, and it keeps
                      the control the same width empty as full — so a row of
                      route fields does not jump as countries are chosen. */}
                  {flagOnly ? (
                    <CircleFlag code={null} size={18} />
                  ) : (
                    (placeholder ?? t('common.form.countryPlaceholder'))
                  )}
                </span>
              )
            }
            return (
              <>
                <CircleFlag code={code} size={18} />
                {flagOnly ? null : (
                  <span className="text-text-body truncate">
                    {country ? countryName(country, language) : code}
                  </span>
                )}
              </>
            )
          }}
        </ComboboxValue>
      </ComboboxTrigger>

      {/* Submits inside a plain `<form>` and is found by autofill, the same
          hidden mirror Select carries. */}
      {name ? <input type="hidden" name={name} value={stored} /> : null}

      {/* The popup normally matches the trigger (`--anchor-width`). A flag-only
          trigger is about sixty pixels wide, and a 249-row list of country
          names in sixty pixels is a column of ellipses — so that one case pins
          a readable width instead and lets `--available-width` cap it on a
          narrow screen. */}
      <ComboboxContent
        className={
          flagOnly ? 'w-[min(18rem,var(--available-width))]' : undefined
        }
      >
        <ComboboxSearch placeholder={t('common.form.countrySearch')} />

        <ComboboxEmpty hint={t('common.form.noMatchesHint')}>
          {t('common.form.noMatches')}
        </ComboboxEmpty>

        <ComboboxList>
          {(code: string) => {
            const option = byCode.get(code)
            if (!option) return null
            return (
              <ComboboxItem key={code} value={code}>
                {/* A legacy place gets the globe rather than a flag, which is
                    CircleFlag's own fallback — nothing special is needed here. */}
                <CircleFlag code={code} size={18} />
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {/* The alpha-2, one step down from the name and muted — it is
                    the secondary reading of the same row, not a second label.
                    Not font-light: at this size the weight thinned "CN" into
                    something that looked disabled rather than quiet. */}
                <span className="text-text-muted shrink-0 text-[12px]">
                  {option.hint}
                </span>
              </ComboboxItem>
            )
          }}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}
